import { apiClient } from "../../../lib/api-client"
import { VacancyDto } from "../dtos/VacancyDto"
import { VacancyListItemDto } from "../dtos/VacancyListItemDto"
import { CreateVacancyCommand } from "../dtos/CreateVacancyDto"
import { VacancyStatus } from "../enums/VacancyStatus"
import { SeniorityLevel } from "../enums/SeniorityLevel"
import { LocationType } from "../enums/LocationType"
import { JobType } from "../enums/JobType"
import { WeightedSkill } from "../entities/WeightedSkill"
import { Department } from "../enums/Department"
import { SageOriginMetadataDto } from "./sage300-service"

function parseWeightedSkills(value: unknown): WeightedSkill[] | undefined {
  if (!value) return undefined
  try {
    const raw: { Name: string; Weight: number }[] =
      typeof value === "string" ? JSON.parse(value) : value
    if (!Array.isArray(raw)) return undefined
    return raw.map((item) => ({
      name: item.Name ?? item.Name,
      weight: item.Weight ?? item.Weight,
    }))
  } catch {
    return undefined
  }
}

function deserializeVacancySkills<T extends Record<string, unknown>>(
  vacancy: T,
): T {
  const skillFields = [
    "keyResponsibilities",
    "requiredSkills",
    "optionalSkills",
    "certifications",
  ] as const
  const result = { ...vacancy } as Record<string, unknown>
  for (const field of skillFields) {
    if (field in result) {
      result[field] = parseWeightedSkills(result[field])
    }
  }
  return result as T
}

export interface AiGeneratedVacancyDto {
  title: string
  description: string
  seniorityLevel: SeniorityLevel
  keyResponsibilities: string[]
  requiredSkills: string[]
  optionalSkills: string[]
  certifications: string[]
  yearsOfExperience: number | null
  educationLevel: string | null
  locationType: LocationType
  jobType: JobType
  department: Department
  minimumSalary: number | null
  maximumSalary: number | null
  sageMetadata?: SageOriginMetadataDto | null
}

class VacancyService {
  async getVacancies(
    organizationId: string,
    searchTerm?: string,
  ): Promise<VacancyListItemDto[]> {
    const response = await apiClient.get(
      `/organizations/${organizationId}/vacancies`,
      {
        params: { searchTerm },
      },
    )
    return response.data.map(deserializeVacancySkills) as VacancyListItemDto[]
  }

  async getVacanciesByStatus(
    status: VacancyStatus,
  ): Promise<VacancyListItemDto[]> {
    const response = await apiClient.get(`/vacancy/status/${status}`)
    return response.data.map(deserializeVacancySkills) as VacancyListItemDto[]
  }

  async getVacancyById(
    organizationId: string,
    id: string,
  ): Promise<VacancyDto> {
    const response = await apiClient.get(
      `/organizations/${organizationId}/vacancies/${id}`,
    )
    return response.data as VacancyDto
  }

  async createVacancy(command: CreateVacancyCommand): Promise<VacancyDto> {
    const response = await apiClient.post(
      `/organizations/${command.organizationId}/vacancies`,
      command,
    )
    return response.data
  }

  async updateVacancy(
    id: string,
    command: CreateVacancyCommand,
  ): Promise<void> {
    await apiClient.put(
      `/organizations/${command.organizationId}/vacancies/${id}`,
      { ...command, Id: id },
    )
  }
  async updateVacancyStatus(
    organizationId: string,
    vacancyId: string,
    status: VacancyStatus,
  ): Promise<boolean> {
    const response = await apiClient.patch(
      `/organizations/${organizationId}/vacancies/${vacancyId}/status`,
      { status },
    )
    return response.data
  }

  async deleteVacancy(organizationId: string, id: string): Promise<void> {
    await apiClient.delete(`/organizations/${organizationId}/vacancies/${id}`)
  }

  async generateVacancyWithAI(
    prompt: string,
    organizationId: string,
  ): Promise<AiGeneratedVacancyDto> {
    const response = await apiClient.post(`/ai/generate-vacancy`, {
      prompt,
      organizationId,
    })
    const result = response.data as { isSuccess: boolean; value?: AiGeneratedVacancyDto; error?: string }
    if (!result.isSuccess || !result.value) {
      throw new Error(result.error ?? "Failed to generate vacancy.")
    }
    return result.value
  }
  async notifyClosedVacancyCandidates(
    organizationId: string,
    vacancyId: string,
  ): Promise<{ isSuccess: boolean; value?: number; error?: string }> {
    const response = await apiClient.post(
      `/organizations/${organizationId}/vacancies/${vacancyId}/notify-pending`,
    )

    return response.data
  }

  async postVacancyToCareers24(
    organizationId: string,
    vacancyId: string,
  ): Promise<{ success: boolean; message?: string; vacancyUrl?: string }> {
    const response = await apiClient.post(
      `/organizations/${organizationId}/vacancies/${vacancyId}/post-to-careers24`,
    )

    return response.data
  }
}

export const vacancyService = new VacancyService()
